import { ChevronDown, ChevronRight, History as HistoryIcon } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { DeleteEntryDialog } from "@/components/time-entries/history/DeleteEntryDialog";
import { EntryEditDialog } from "@/components/time-entries/history/EntryEditDialog";
import { HistoryEntryRow } from "@/components/time-entries/history/HistoryEntryRow";
import {
  defaultFilters,
  filtersToListParams,
  type HistoryFilterState,
  HistoryFilters,
} from "@/components/time-entries/history/HistoryFilters";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { type TimeEntry, useHistorySummary, useTimeEntries } from "@/hooks/use-time-entries";

const FILTER_STORAGE_KEY = "usg-history-filters-v1";

function loadStoredFilters(): HistoryFilterState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(FILTER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<HistoryFilterState>;
    return { ...defaultFilters(), ...parsed };
  } catch {
    return null;
  }
}

function getMonday(dateISO: string): Date {
  const d = new Date(`${dateISO}T00:00:00`);
  const dow = d.getDay();
  const monOffset = dow === 0 ? -6 : 1 - dow;
  const mon = new Date(d);
  mon.setDate(d.getDate() + monOffset);
  mon.setHours(0, 0, 0, 0);
  return mon;
}

function fmtDateISO(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function fmtShort(d: Date) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function formatHours(n: number) {
  return (Math.round(n * 10) / 10).toFixed(1);
}

interface WeekBucket {
  weekStart: string;
  weekEnd: string;
  label: string;
  entries: TimeEntry[];
  billable: number;
  overhead: number;
  total: number;
  status: "draft" | "submitted" | "approved" | "flagged" | "empty";
}

const STATUS_PRECEDENCE: Record<string, number> = {
  flagged: 4,
  submitted: 3,
  draft: 2,
  approved: 1,
};

function rollupStatus(entries: TimeEntry[]): WeekBucket["status"] {
  if (entries.length === 0) return "empty";
  let best: WeekBucket["status"] = "approved";
  let bestRank = 0;
  for (const e of entries) {
    const rank = STATUS_PRECEDENCE[e.status] ?? 0;
    if (rank > bestRank) {
      bestRank = rank;
      best = e.status as WeekBucket["status"];
    }
  }
  return best;
}

function bucketByWeek(entries: TimeEntry[]): WeekBucket[] {
  const map = new Map<string, TimeEntry[]>();
  for (const e of entries) {
    const monISO = fmtDateISO(getMonday(e.entryDate));
    const list = map.get(monISO) ?? [];
    list.push(e);
    map.set(monISO, list);
  }

  const buckets: WeekBucket[] = [];
  for (const [weekStart, weekEntries] of map.entries()) {
    const mon = new Date(`${weekStart}T00:00:00`);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    weekEntries.sort((a, b) =>
      a.entryDate > b.entryDate ? -1 : a.entryDate < b.entryDate ? 1 : 0,
    );
    const billable = weekEntries
      .filter((e) => e.type === "billable")
      .reduce((s, e) => s + e.hours, 0);
    const overhead = weekEntries
      .filter((e) => e.type === "overhead")
      .reduce((s, e) => s + e.hours, 0);
    buckets.push({
      weekStart,
      weekEnd: fmtDateISO(sun),
      label: `${fmtShort(mon)} – ${fmtShort(sun)}`,
      entries: weekEntries,
      billable,
      overhead,
      total: billable + overhead,
      status: rollupStatus(weekEntries),
    });
  }

  buckets.sort((a, b) => (a.weekStart > b.weekStart ? -1 : a.weekStart < b.weekStart ? 1 : 0));
  return buckets;
}

interface WeekGroupProps {
  bucket: WeekBucket;
  initialOpen: boolean;
  onEdit: (entry: TimeEntry) => void;
  onDelete: (entry: TimeEntry) => void;
  onResolveFlag: (entry: TimeEntry) => void;
}

function WeekGroup({ bucket, initialOpen, onEdit, onDelete, onResolveFlag }: WeekGroupProps) {
  const [open, setOpen] = useState(initialOpen);
  return (
    <div className="entry-day">
      <button type="button" className="entry-day__header" onClick={() => setOpen((o) => !o)}>
        <div className="entry-day__date">
          {open ? (
            <ChevronDown size={14} color="#6B7280" />
          ) : (
            <ChevronRight size={14} color="#6B7280" />
          )}
          <span>Week of {bucket.label}</span>
          <small>
            · {bucket.entries.length} {bucket.entries.length === 1 ? "entry" : "entries"}
          </small>
          {bucket.status !== "empty" && (
            <span style={{ marginLeft: 8 }}>
              <StatusBadge status={bucket.status} />
            </span>
          )}
        </div>
        <div className="entry-day__total">
          <em>Billable</em>
          {formatHours(bucket.billable)}
          <em style={{ marginLeft: 12 }}>Overhead</em>
          {formatHours(bucket.overhead)}
          <em style={{ marginLeft: 12 }}>Total</em>
          <strong>{formatHours(bucket.total)} hrs</strong>
        </div>
      </button>
      {open &&
        bucket.entries.map((e) => (
          <HistoryEntryRow
            key={e.id}
            entry={e}
            onEdit={onEdit}
            onDelete={onDelete}
            onResolveFlag={onResolveFlag}
          />
        ))}
    </div>
  );
}

export function HistoryPage() {
  const [filters, setFilters] = useState<HistoryFilterState>(
    () => loadStoredFilters() ?? defaultFilters(),
  );
  const [editEntry, setEditEntry] = useState<TimeEntry | null>(null);
  const [deleteEntry, setDeleteEntry] = useState<TimeEntry | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.sessionStorage.setItem(FILTER_STORAGE_KEY, JSON.stringify(filters));
  }, [filters]);

  const listParams = useMemo(() => filtersToListParams(filters), [filters]);
  const summaryParams = useMemo(() => {
    const { limit: _limit, cursor: _cursor, ...rest } = listParams;
    return rest;
  }, [listParams]);

  const { data, isLoading, isError, error, refetch } = useTimeEntries(listParams);
  const { data: summary } = useHistorySummary(summaryParams);

  const entries = data?.data ?? [];
  const buckets = useMemo(() => bucketByWeek(entries), [entries]);

  // Refresh open entry from latest data so the dialog reflects server state after invalidation
  const liveEditEntry = useMemo(
    () => (editEntry ? (entries.find((e) => e.id === editEntry.id) ?? editEntry) : null),
    [editEntry, entries],
  );

  return (
    <>
      <div className="topbar">
        <div className="topbar__crumbs">
          <span>Engineering</span>
          <ChevronRight size={14} />
          <strong>History</strong>
        </div>
        <div className="topbar__actions">
          <span className="t-small t-muted">All times in your local timezone</span>
        </div>
      </div>

      <div className="page">
        <div className="page-header">
          <div>
            <div className="page-header__eyebrow">
              <HistoryIcon size={14} /> Time Entry History
            </div>
            <h1 className="page-header__title">Your time entry history</h1>
            <p className="page-header__sub">
              Review past entries, fix drafts, and resolve anything flagged by your manager.
            </p>
          </div>
        </div>

        <HistoryFilters value={filters} onChange={setFilters} />

        {summary && (
          <div className="kpi-row" style={{ marginBottom: 16 }}>
            <div className="kpi">
              <div className="kpi__label">Entries</div>
              <div className="kpi__value">{summary.totalEntries}</div>
              <div className="kpi__accent" style={{ background: "var(--blue)" }} />
            </div>
            <div className="kpi">
              <div className="kpi__label">Total Hours</div>
              <div className="kpi__value">
                {formatHours(summary.totalHours)}
                <span className="kpi__unit">hrs</span>
              </div>
              <div className="kpi__accent" />
            </div>
            <div className="kpi">
              <div className="kpi__label">Billable</div>
              <div className="kpi__value">
                {formatHours(summary.billableHours)}
                <span className="kpi__unit">hrs</span>
              </div>
              <div className="kpi__accent" style={{ background: "var(--green-primary)" }} />
            </div>
            <div className="kpi">
              <div className="kpi__label">Overhead</div>
              <div className="kpi__value">
                {formatHours(summary.overheadHours)}
                <span className="kpi__unit">hrs</span>
              </div>
              <div className="kpi__accent" style={{ background: "var(--amber)" }} />
            </div>
          </div>
        )}

        <div className="card">
          <div className="card__header">
            <h2 className="h-section">Entries by week</h2>
          </div>
          <div className="entry-table">
            {isError ? (
              <div style={{ padding: 24 }}>
                <ErrorState
                  message={error instanceof Error ? error.message : "Couldn't load history"}
                  onRetry={() => refetch()}
                />
              </div>
            ) : isLoading ? (
              <div style={{ padding: "32px 24px" }}>
                <div className="skeleton-row" style={{ height: 18, marginBottom: 12 }} />
                <div
                  className="skeleton-row"
                  style={{ height: 18, marginBottom: 12, width: "80%" }}
                />
                <div className="skeleton-row" style={{ height: 18, width: "60%" }} />
              </div>
            ) : buckets.length === 0 ? (
              <div style={{ padding: "48px 24px", textAlign: "center" }}>
                <p className="t-muted" style={{ marginBottom: 4 }}>
                  No entries match the current filters.
                </p>
                <p className="t-small t-muted">Adjust the filters above or clear them.</p>
              </div>
            ) : (
              buckets.map((b, idx) => (
                <WeekGroup
                  key={b.weekStart}
                  bucket={b}
                  initialOpen={idx < 4}
                  onEdit={setEditEntry}
                  onDelete={setDeleteEntry}
                  onResolveFlag={setEditEntry}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {liveEditEntry && (
        <EntryEditDialog
          key={liveEditEntry.id}
          entry={liveEditEntry}
          open
          onClose={() => setEditEntry(null)}
        />
      )}
      {deleteEntry && (
        <DeleteEntryDialog
          key={deleteEntry.id}
          entry={deleteEntry}
          open
          onClose={() => setDeleteEntry(null)}
        />
      )}
    </>
  );
}
